import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, Eye, ToggleLeft, ToggleRight, MessageCircle, Info } from 'lucide-react';
import { api } from '@/services/api';

interface WhatsAppTemplate {
  id: string;
  companyId: string;
  eventType: string;
  messageBody: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const EVENT_TYPE_LABELS: Record<string, { label: string; description: string }> = {
  SERVICE_CREATED: {
    label: 'Service Received',
    description: 'Sent when a new service is booked for the customer',
  },
  SERVICE_IN_PROGRESS: {
    label: 'Service In Process',
    description: 'Sent when repair work begins on the device',
  },
  SERVICE_COMPLETED: {
    label: 'Device Ready',
    description: 'Sent when device repair is completed and ready for collection',
  },
  SERVICE_NOT_READY: {
    label: 'Device Not Ready',
    description: 'Sent when device cannot be repaired',
  },
  SERVICE_DELIVERED: {
    label: 'Service Delivered',
    description: 'Sent when device is handed back to the customer',
  },
};

const PLACEHOLDERS = [
  { key: '{customerName}', desc: 'Customer name' },
  { key: '{ticketNumber}', desc: 'Service ticket number' },
  { key: '{deviceModel}', desc: 'Device model name' },
  { key: '{branchName}', desc: 'Branch name' },
  { key: '{branchPhone}', desc: 'Branch phone number' },
  { key: '{companyName}', desc: 'Company name' },
];

const whatsappTemplateApi = {
  getAll: async (): Promise<WhatsAppTemplate[]> => {
    const response = await api.get('/settings/whatsapp-templates');
    return response.data.data;
  },
  update: async (eventType: string, data: { messageBody?: string; isActive?: boolean }): Promise<WhatsAppTemplate> => {
    const response = await api.put(`/settings/whatsapp-templates/${eventType}`, data);
    return response.data.data;
  },
  preview: async (messageBody: string): Promise<string> => {
    const response = await api.post('/settings/whatsapp-templates/preview', { messageBody });
    return response.data.data.preview;
  },
};

export default function WhatsAppTemplateSettings() {
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [previewText, setPreviewText] = useState<string | null>(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['whatsapp-templates'],
    queryFn: whatsappTemplateApi.getAll,
    staleTime: 5 * 60 * 1000,
  });

  const updateMutation = useMutation({
    mutationFn: ({ eventType, data }: { eventType: string; data: { messageBody?: string; isActive?: boolean } }) =>
      whatsappTemplateApi.update(eventType, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-templates'] });
      toast.success('Template updated');
      setEditingTemplate(null);
      setPreviewText(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update template');
    },
  });

  const handleEdit = (template: WhatsAppTemplate) => {
    setEditingTemplate(template.eventType);
    setEditBody(template.messageBody);
    setPreviewText(null);
  };

  const handleSave = (eventType: string) => {
    updateMutation.mutate({ eventType, data: { messageBody: editBody } });
  };

  const handleToggle = (template: WhatsAppTemplate) => {
    updateMutation.mutate({
      eventType: template.eventType,
      data: { isActive: !template.isActive },
    });
  };

  const handlePreview = async () => {
    try {
      const preview = await whatsappTemplateApi.preview(editBody);
      setPreviewText(preview);
    } catch {
      toast.error('Failed to generate preview');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-green-600" />
          WhatsApp Message Templates
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Configure the messages sent to customers at each stage of the service lifecycle.
          Messages are sent automatically when WhatsApp notification is enabled for a service.
        </p>
      </div>

      {/* Placeholder Reference */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-800">Available Placeholders</p>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
              {PLACEHOLDERS.map((p) => (
                <span key={p.key} className="text-xs text-blue-700">
                  <code className="bg-blue-100 px-1 py-0.5 rounded">{p.key}</code> - {p.desc}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Templates List */}
      <div className="space-y-4">
        {templates.map((template) => {
          const meta = EVENT_TYPE_LABELS[template.eventType] || {
            label: template.eventType,
            description: '',
          };
          const isEditing = editingTemplate === template.eventType;

          return (
            <div
              key={template.id}
              className={`border rounded-lg overflow-hidden ${
                template.isActive ? 'border-gray-200' : 'border-gray-100 opacity-60'
              }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">{meta.label}</h4>
                  <p className="text-xs text-gray-500">{meta.description}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleToggle(template)}
                    disabled={updateMutation.isPending}
                    className="flex items-center gap-1 text-xs"
                    title={template.isActive ? 'Disable' : 'Enable'}
                  >
                    {template.isActive ? (
                      <ToggleRight className="w-6 h-6 text-green-600" />
                    ) : (
                      <ToggleLeft className="w-6 h-6 text-gray-400" />
                    )}
                    <span className={template.isActive ? 'text-green-600' : 'text-gray-400'}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="p-4">
                {isEditing ? (
                  <div className="space-y-3">
                    <textarea
                      value={editBody}
                      onChange={(e) => {
                        setEditBody(e.target.value);
                        setPreviewText(null);
                      }}
                      rows={5}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                      placeholder="Enter message template..."
                    />
                    {previewText && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-xs font-medium text-green-800 mb-1">Preview:</p>
                        <p className="text-sm text-green-900 whitespace-pre-wrap">{previewText}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSave(template.eventType)}
                        disabled={updateMutation.isPending}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-xs disabled:opacity-50"
                      >
                        {updateMutation.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Save className="h-3.5 w-3.5" />
                        )}
                        Save
                      </button>
                      <button
                        onClick={handlePreview}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Preview
                      </button>
                      <button
                        onClick={() => {
                          setEditingTemplate(null);
                          setPreviewText(null);
                        }}
                        className="px-3 py-1.5 text-gray-500 hover:text-gray-700 text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => handleEdit(template)}
                    className="cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
                  >
                    <p className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                      {template.messageBody}
                    </p>
                    <p className="text-xs text-gray-400 mt-2">Click to edit</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {templates.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No templates found. Templates will be auto-created when you first open this page.
        </div>
      )}
    </div>
  );
}
